import { c as _c } from "react/compiler-runtime";
// «Уведомления» по макету «Профиль» (кадры 10–12). Лента — белые карточки r20
// 810 через 16: оранжевая точка непрочитанного (Ø11 #FA5C2E), заголовок 14px
// жирный, текст 14px, «×» скрывает уведомление. Под заголовком — чипы активных
// фильтров (h32 pill, заливка #FA5C2E 5 %, обводка 0.5 #FA5C2E, «×»). Справа —
// «Фильтры» (темы, «Сбросить» / «Показать N») и шестерёнка «Настройка
// уведомлений» (темы с чекбоксами #FF5E00 и подписями, «Отключить все
// уведомления» / «Сохранить»). Внизу — «Очистить все» 40px r10.
// Темы — производная от вида уведомления (контракт NotificationTopic).

import { PushToggle } from "@/components/pwa/push-toggle";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { Skeleton } from "@osmix/ui/skeleton";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type NotificationItem } from "@/api";
import type { NotificationTopic, NotificationTopicSettings } from "@/api/types";
import { notificationKeys, useMarkNotificationRead } from "@/api/use-notifications";
import { ErrorState } from "@/components/common/states";
import { ProfilePanel } from "@/components/profile/profile-shell";
import { resolveBuyerNotificationAction } from "@/lib/notification-action";
import { FilterIcon, SettingsIcon, XIcon, XSmall16Icon, XSmall20Icon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store";
const OVERLAY = "bg-black/20 backdrop-blur-[2px]";
const TOPICS: Array<{
  id: NotificationTopic;
  title: string;
  chip: string;
  hints: string[];
}> = [{
  id: "cart",
  title: "Корзина",
  chip: "Корзина",
  hints: ["Напоминание о забытых товарах в корзине", "Снижение цены на товары из корзины", "Заканчивается наличие товаров в корзине"]
}, {
  id: "delivery",
  title: "Доставки",
  chip: "Доставки",
  hints: ["Отправка заказа и прибытие в пункт выдачи"]
}, {
  id: "orders",
  title: "Заказы",
  chip: "Заказы",
  hints: ["Подтверждение оформления заказа", "Проблема с заказом"]
}, {
  id: "returns",
  title: "Возвраты",
  chip: "Возвраты",
  hints: ["Заявка на возврат принята", "Статус возврата"]
}, {
  id: "likes",
  title: "Мне нравится",
  chip: "Мне нравится",
  hints: ["Снижение цены на понравившийся товар", "Товар из избранного заканчивается"]
}, {
  id: "shops",
  title: "Любимые магазины",
  chip: "Любимые магазины",
  hints: ["Новинки от избранных брендов", "Спецпредложения от любимых магазинов"]
}, {
  id: "reviews",
  title: "Отзывы",
  // Вопросов к товару больше нет (правка №2, Элина 16.10.2026).
  chip: "Отзывы",
  hints: ["Магазин ответил на ваш отзыв", "Продавец оставил комментарий к вашему отзыву"]
}, {
  id: "payment",
  title: "Способ оплаты / Оплата",
  chip: "Способ оплаты/Оплата",
  hints: ["Обновление платёжной информации", "Ошибка при оплате заказа", "Уведомление об окончании срока действия карты", "Чек в ПДФ"]
}, {
  id: "support",
  title: "Поддержка",
  chip: "Поддержка",
  hints: ["Ответ службы поддержки на ваш запрос", "Оценить работу поддержки"]
}];
const CHIP_OF = Object.fromEntries(TOPICS.map(t => [t.id, t.chip])) as Record<NotificationTopic, string>;

// fd-notif-17: фильтр по статусу — отдельная строка чипов в «Фильтрах» над темами.
type ReadStatus = "all" | "unread" | "read";
const STATUSES: Array<{
  id: ReadStatus;
  chip: string;
}> = [{
  id: "all",
  chip: "Все"
}, {
  id: "unread",
  chip: "Непрочитанные"
}, {
  id: "read",
  chip: "Прочитанные"
}];
const STATUS_CHIP = Object.fromEntries(STATUSES.map(s => [s.id, s.chip])) as Record<ReadStatus, string>;
export function ProfileNotificationsPage() {
  const principalId = useProfile(s => s.user?.id ?? "guest");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [topics, setTopics] = useState<NotificationTopic[]>([]);
  const [status, setStatus] = useState<ReadStatus>("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const settingsOpen = params.get("settings") === "1";
  const setSettingsOpen = (open: boolean) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (open) next.set("settings", "1");else next.delete("settings");
    return next;
  }, {
    replace: true
  });
  const markRead = useMarkNotificationRead();
  const topicKey = [...topics].sort().join(",");
  const feed = useInfiniteQuery({
    queryKey: [...notificationKeys.listRoot(principalId), "topics", topicKey, "status", status],
    queryFn: ({
      pageParam
    }) => api.notifications.list({
      cursor: pageParam,
      limit: 20,
      filter: status,
      locale: "ru",
      ...(topicKey ? {
        topics: topicKey
      } : {})
    }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.hasMore ? last.nextCursor ?? undefined : undefined
  });
  const items = feed.data?.pages.flatMap(p => p.items) ?? [];
  const refresh = () => qc.invalidateQueries({
    queryKey: notificationKeys.root(principalId)
  });
  const archive = async (item: NotificationItem) => {
    qc.setQueriesData<{
      pages: Array<{
        items: NotificationItem[];
      }>;
    }>({
      queryKey: notificationKeys.listRoot(principalId)
    }, data => data ? {
      ...data,
      pages: data.pages.map(p_0 => ({
        ...p_0,
        items: p_0.items.filter(i => i.id !== item.id)
      }))
    } : data);
    try {
      await api.notifications.archive(item.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось скрыть уведомление");
    } finally {
      void refresh();
    }
  };
  const clearAll = async () => {
    try {
      await api.notifications.archiveAll(topics);
      toast.success("Уведомления очищены");
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось очистить уведомления");
    } finally {
      void refresh();
    }
  };
  const open_0 = (item_0: NotificationItem) => {
    if (!item_0.readAt) markRead.mutate(item_0.id);
    const to = resolveBuyerNotificationAction(item_0.action);
    if (to) navigate(to);
  };
  return <ProfilePanel title="Уведомления" actions={<>
					<IconButton label="Фильтры" onClick={() => setFiltersOpen(true)}>
						<FilterIcon size={24} />
					</IconButton>
					<IconButton label="Настройка уведомлений" onClick={() => setSettingsOpen(true)}>
						<SettingsIcon size={24} />
					</IconButton>
				</>}>
			{(topics.length > 0 || status !== "all") && <div className="mb-4 flex flex-wrap gap-2.5">
					{status !== "all" && <span className="inline-flex h-8 items-center gap-1.5 rounded-full border-[0.5px] border-[#FA5C2E] bg-[#FA5C2E]/5 px-4 text-[14px] leading-[18px] font-medium text-[#1F1F1F]">
							{STATUS_CHIP[status]}
							<button type="button" aria-label={`Убрать фильтр «${STATUS_CHIP[status]}»`} onClick={() => setStatus("all")} className="grid size-4 place-items-center rounded-full text-[#333] outline-none hover:text-[#FA5C2E] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
								<XSmall16Icon size={16} />
							</button>
						</span>}
					{topics.map(t => <span key={t} className="inline-flex h-8 items-center gap-1.5 rounded-full border-[0.5px] border-[#FA5C2E] bg-[#FA5C2E]/5 px-4 text-[14px] leading-[18px] font-medium text-[#1F1F1F]">
							{CHIP_OF[t]}
							<button type="button" aria-label={`Убрать фильтр «${CHIP_OF[t]}»`} onClick={() => setTopics(prev_0 => prev_0.filter(x => x !== t))} className="grid size-4 place-items-center rounded-full text-[#333] outline-none hover:text-[#FA5C2E] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
								<XSmall16Icon size={16} />
							</button>
						</span>)}
				</div>}

			{feed.isPending ? <div className="flex flex-col gap-4">
					{[0, 1, 2].map(i_0 => <Skeleton key={i_0} className="h-20 rounded-[20px] bg-white" />)}
				</div> : feed.isError ? <ErrorState onRetry={() => void feed.refetch()} /> : items.length === 0 ? <div className="rounded-[20px] bg-white px-6 py-14 text-center">
					<p className="text-[16px] font-medium text-[#161616]">Уведомлений нет</p>
					<p className="mt-1 text-[14px] text-[#1F1F1F]/48">
						{status === "unread" ? "Все уведомления прочитаны." : status === "read" ? "Прочитанных уведомлений пока нет." : topics.length ? "По выбранным темам пока ничего нет." : "Здесь появятся новости о заказах, возвратах и ответах магазинов."}
					</p>
				</div> : <>
					<ul className="flex flex-col gap-4">
						{items.map(item_1 => <li key={item_1.id}>
								<div role="button" tabIndex={0} onClick={() => open_0(item_1)} onKeyDown={e_1 => {
            if (e_1.key === "Enter") open_0(item_1);
          }} className="relative cursor-pointer rounded-[20px] bg-white p-2.5 pr-10 outline-none transition-shadow hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
									<p className="flex min-h-5 items-start gap-1.5 py-px text-[14px] leading-[18px] font-semibold text-black">
										{!item_1.readAt && <span className="mt-[3.5px] size-[11px] shrink-0 rounded-full bg-[#FA5C2E]">
												<span className="sr-only">Непрочитано</span>
											</span>}
										<span>{item_1.title}</span>
									</p>
									<p className="mt-1 text-[14px] leading-[18px] whitespace-pre-line text-black">{item_1.body}</p>
									<button type="button" aria-label="Скрыть уведомление" onClick={e_2 => {
              e_2.stopPropagation();
              void archive(item_1);
            }} className="absolute top-2.5 right-2.5 grid size-5 place-items-center rounded-full text-[#333] outline-none hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
										<XSmall20Icon size={20} />
									</button>
								</div>
							</li>)}
					</ul>
					{feed.hasNextPage && <button type="button" disabled={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()} className="mt-4 self-center text-[14px] text-[#1F1F1F]/64 outline-none hover:text-[#1F1F1F] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
							{feed.isFetchingNextPage ? "Загружаем…" : "Показать ещё"}
						</button>}
					<button type="button" onClick={() => void clearAll()} className="mt-4 h-10 self-start rounded-[10px] bg-[#989AA2]/12 px-4 text-[14px] leading-[18px] font-semibold text-[#1F1F1F] outline-none hover:bg-[#989AA2]/20 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
						Очистить все
					</button>
				</>}

			<FiltersDialog open={filtersOpen} onOpenChange={setFiltersOpen} value={topics} status={status} onApply={(nextTopics, nextStatus) => {
      setTopics(nextTopics);
      setStatus(nextStatus);
    }} />
			<SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={() => void refresh()} />
		</ProfilePanel>;
}
function IconButton(t0) {
  const $ = _c(4);
  const {
    label,
    onClick,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== label || $[2] !== onClick) {
    t1 = <button type="button" aria-label={label} title={label} onClick={onClick} className="grid size-8 place-items-center rounded-[8px] text-[#333] outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{children}</button>;
    $[0] = children;
    $[1] = label;
    $[2] = onClick;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ModalHeader(t0) {
  const $ = _c(8);
  const {
    title,
    onClose
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <DialogTitle className="text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#161616]">{title}</DialogTitle>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <XIcon size={24} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose) {
    t3 = <button type="button" aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C"} onClick={onClose} className="grid size-7 place-items-center rounded-full text-[#1F1F1F] outline-none hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t2}</button>;
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <div className="flex items-center justify-between gap-3">{t1}{t3}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

// «Фильтры» (кадр 12): чипы тем h32 pill (#989AA2 12 %, выбранный — оранжевый
// контур), «Сбросить» / «Показать N» 50/50 h40 r10. Над темами — строка
// статуса «Все / Непрочитанные / Прочитанные» теми же чипами (fd-notif-17).
function FiltersDialog(t0) {
  const $ = _c(55);
  const {
    open,
    onOpenChange,
    value,
    status,
    onApply
  } = t0;
  const principalId = useProfile(_temp);
  const [draft, setDraft] = useState(value);
  const [statusDraft, setStatusDraft] = useState(status);
  let t1;
  let t2;
  if ($[0] !== open || $[1] !== status || $[2] !== value) {
    t1 = () => {
      if (open) {
        setDraft(value);
        setStatusDraft(status);
      }
    };
    t2 = [open, value, status];
    $[0] = open;
    $[1] = status;
    $[2] = value;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== principalId || $[6] !== statusDraft) {
    t3 = [...notificationKeys.root(principalId), "topic-counts", statusDraft];
    $[5] = principalId;
    $[6] = statusDraft;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== statusDraft) {
    t4 = () => api.notifications.topicCounts(statusDraft);
    $[8] = statusDraft;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== open || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled: open
    };
    $[10] = open;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const counts = useQuery(t5);
  let t6;
  if ($[14] !== counts.data?.topics || $[15] !== counts.data?.total || $[16] !== draft) {
    t6 = draft.length ? draft.reduce((sum, t) => sum + (counts.data?.topics[t] ?? 0), 0) : counts.data?.total ?? 0;
    $[14] = counts.data?.topics;
    $[15] = counts.data?.total;
    $[16] = draft;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const shown = t6;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = t_0 => setDraft(prev => prev.includes(t_0) ? prev.filter(x => x !== t_0) : [...prev, t_0]);
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const toggle = t7;
  let t8;
  if ($[19] !== onOpenChange) {
    t8 = <ModalHeader title={"\u0424\u0438\u043B\u044C\u0442\u0440\u044B"} onClose={() => onOpenChange(false)} />;
    $[19] = onOpenChange;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t10;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogDescription className="sr-only">Статус и темы уведомлений</DialogDescription>;
    t10 = <p className="mt-3 text-[14px] leading-[18px] font-medium text-[#1F1F1F]/64">Статус</p>;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  let t11;
  if ($[23] !== statusDraft) {
    t11 = STATUSES.map(s_0 => {
      const active = statusDraft === s_0.id;
      return <button key={s_0.id} type="button" role="radio" aria-checked={active} onClick={() => setStatusDraft(s_0.id)} className={cn("h-8 rounded-full border-[0.5px] px-3 text-[14px] leading-[18px] font-medium text-[#1F1F1F] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", active ? "border-[#FA5C2E] bg-[#FA5C2E]/5" : "border-[#1F1F1F]/4 bg-[#1F1F1F]/3 hover:bg-[#1F1F1F]/6")}>{s_0.chip}</button>;
    });
    $[23] = statusDraft;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <div className="mt-2 flex flex-wrap gap-2.5" role="radiogroup" aria-label={"\u0421\u0442\u0430\u0442\u0443\u0441"}>{t11}</div>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="mt-4 text-[14px] leading-[18px] font-medium text-[#1F1F1F]/64">Темы</p>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== draft) {
    t14 = TOPICS.map(t_1 => {
      const active_0 = draft.includes(t_1.id);
      return <button key={t_1.id} type="button" aria-pressed={active_0} onClick={() => toggle(t_1.id)} className={cn("h-8 rounded-full border-[0.5px] px-3 text-[14px] leading-[18px] font-medium text-[#1F1F1F] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", active_0 ? "border-[#FA5C2E] bg-[#FA5C2E]/5" : "border-[#1F1F1F]/4 bg-[#1F1F1F]/3 hover:bg-[#1F1F1F]/6")}>{t_1.chip}</button>;
    });
    $[28] = draft;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <div className="mt-2 flex flex-wrap gap-2.5">{t14}</div>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = draft.length === 0 && statusDraft === "all";
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setDraft([]);
      setStatusDraft("all");
    };
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t16) {
    t18 = <button type="button" disabled={t16} onClick={t17} className="h-10 rounded-[10px] bg-[#989AA2]/12 text-[14px] leading-[18px] font-semibold text-[#1F1F1F] outline-none transition-colors hover:bg-[#989AA2]/20 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:text-[#8A8A8B]/46 disabled:hover:bg-[#989AA2]/12">Сбросить</button>;
    $[33] = t16;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== draft || $[36] !== onApply || $[37] !== onOpenChange || $[38] !== statusDraft) {
    t19 = () => {
      onApply(draft, statusDraft);
      onOpenChange(false);
    };
    $[35] = draft;
    $[36] = onApply;
    $[37] = onOpenChange;
    $[38] = statusDraft;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const t20 = counts.isPending ? "" : shown;
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = <button type="button" onClick={t19} className="h-10 rounded-[10px] bg-[#2E2E2E] text-[14px] leading-[18px] font-semibold text-white outline-none hover:bg-[#1F1F1F] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">Показать {t20}</button>;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t18 || $[44] !== t21) {
    t22 = <div className="mt-5 grid grid-cols-2 gap-2.5">{t18}{t21}</div>;
    $[43] = t18;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t12 || $[47] !== t15 || $[48] !== t22 || $[49] !== t8) {
    t23 = <DialogContent overlayClassName={OVERLAY} showCloseButton={false} onOpenAutoFocus={_temp2} className="gap-0 rounded-[20px] border-0 bg-white p-4 pt-[18px] shadow-none sm:max-w-[440px]">{t8}{t9}{t10}{t12}{t13}{t15}{t22}</DialogContent>;
    $[46] = t12;
    $[47] = t15;
    $[48] = t22;
    $[49] = t8;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== onOpenChange || $[52] !== open || $[53] !== t23) {
    t24 = <Dialog open={open} onOpenChange={onOpenChange}>{t23}</Dialog>;
    $[51] = onOpenChange;
    $[52] = open;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
}

// «Настройка уведомлений» (кадр 11): темы с чекбоксами 20 r5 #FF5E00, подписи
// 12px #1F1F1F 64 %; «Отключить все уведомления» (при всех снятых — «Включить
// все») / «Сохранить».
function _temp2(e) {
  return e.preventDefault();
}
function _temp(s) {
  return s.user?.id ?? "guest";
}
function SettingsDialog({
  open,
  onOpenChange,
  onSaved
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const principalId = useProfile(s => s.user?.id ?? "guest");
  const qc = useQueryClient();
  const key = [...notificationKeys.root(principalId), "topic-settings"];
  const settings = useQuery({
    queryKey: key,
    queryFn: () => api.notifications.getTopicSettings(),
    enabled: open
  });
  // Канал «почта» живёт в общих настройках уведомлений (preferences), а не
  // в темах ленты: письма о заказе, оплате и возврате критичны и уходят всегда,
  // выключаются только служебные и рекламные.
  const prefsKey = [...notificationKeys.root(principalId), "preferences-email"];
  const prefs = useQuery({
    queryKey: prefsKey,
    queryFn: () => api.notifications.getPreferences(),
    enabled: open
  });
  const [draft, setDraft] = useState<NotificationTopicSettings | null>(null);
  const [emailDraft, setEmailDraft] = useState<{
    enabled: boolean;
    marketing: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && settings.data) setDraft(settings.data);
    if (!open) setDraft(null);
  }, [open, settings.data]);
  useEffect(() => {
    if (open && prefs.data) setEmailDraft(prefs.data.email ?? {
      enabled: true,
      marketing: true
    });
    if (!open) setEmailDraft(null);
  }, [open, prefs.data]);
  const save = async (next: NotificationTopicSettings, email = emailDraft) => {
    setSaving(true);
    try {
      const saved = await api.notifications.updateTopicSettings(next);
      if (email) qc.setQueryData(prefsKey, await api.notifications.updatePreferences({
        email
      }));
      qc.setQueryData(key, saved);
      toast.success("Настройки уведомлений сохранены");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  };

  // Нижняя кнопка — переключатель черновика, а не сохранение: «Отключить все»
  // снимает все галочки, когда сняты все — она же «Включить все». Окно не
  // закрывается; выход с записью — только «Сохранить» (замечание Айталы 01.10).
  const allUnchecked = draft != null && TOPICS.every(t => !draft[t.id]) && (!emailDraft || !emailDraft.enabled && !emailDraft.marketing);
  const setAll = (on: boolean) => {
    setDraft(d => d ? {
      ...d,
      ...Object.fromEntries(TOPICS.map(t_0 => [t_0.id, on]))
    } as NotificationTopicSettings : d);
    setEmailDraft(d_0 => d_0 ? {
      enabled: on,
      marketing: on
    } : d_0);
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent overlayClassName={OVERLAY} showCloseButton={false} onOpenAutoFocus={e_0 => e_0.preventDefault()} className="max-h-[calc(100dvh-2rem)] gap-0 overflow-y-auto rounded-[20px] border-0 bg-white p-4 pt-[18px] shadow-none sm:max-w-[440px]">
				<ModalHeader title="Настройка уведомлений" onClose={() => onOpenChange(false)} />
				<DialogDescription className="sr-only">Какие уведомления присылать</DialogDescription>
				{!draft ? <div className="mt-3 flex flex-col gap-3">
						{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-10 rounded-[8px]" />)}
					</div> : <ul className="mt-2 flex flex-col gap-2.5">
						{TOPICS.map(t_1 => {
          const on_0 = draft[t_1.id];
          return <li key={t_1.id}>
									<label className="flex cursor-pointer items-start gap-2.5">
										<input type="checkbox" checked={on_0} onChange={e_1 => setDraft(d_1 => d_1 ? {
                ...d_1,
                [t_1.id]: e_1.target.checked
              } : d_1)} className="peer sr-only" />
										<span aria-hidden="true" className={cn("mt-[1px] grid size-5 shrink-0 place-items-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#1F1F1F]/40", on_0 ? "border-[#FF5E00] bg-[#FF5E00] text-white" : "border-[#1F1F1F]/24")}>
											{on_0 && <Check className="size-3.5" strokeWidth={2.5} />}
										</span>
										<span>
											<span className="block text-[16px] leading-[21px] font-medium text-[#1F1F1F]">{t_1.title}</span>
											{t_1.hints.map(h => <span key={h} className="block text-[12px] leading-4 text-black/50">
													{h}
												</span>)}
										</span>
									</label>
								</li>;
        })}
					</ul>}
				{emailDraft && <div className="mt-4 border-t border-[#E2E2E2] pt-3">
						<span className="block text-[14px] font-medium leading-5 text-[#1F1F1F]">Письма на почту</span>
						<ul className="mt-2 flex flex-col gap-2.5">
							{([{
            id: "enabled",
            title: "Доставка и статусы",
            hint: "Заказ доставлен и другие служебные письма"
          }, {
            id: "marketing",
            title: "Цены и поступления",
            hint: "Снижение цены на избранное, товар снова в наличии"
          }] as const).map(row => {
            const on_1 = emailDraft[row.id];
            return <li key={row.id}>
										<label className="flex cursor-pointer items-start gap-2.5">
											<input type="checkbox" checked={on_1} onChange={e_2 => setEmailDraft(d_2 => d_2 ? {
                  ...d_2,
                  [row.id]: e_2.target.checked
                } : d_2)} className="peer sr-only" />
											<span aria-hidden="true" className={cn("mt-[1px] grid size-5 shrink-0 place-items-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#1F1F1F]/40", on_1 ? "border-[#FF5E00] bg-[#FF5E00] text-white" : "border-[#1F1F1F]/24")}>
												{on_1 && <Check className="size-3.5" strokeWidth={2.5} />}
											</span>
											<span>
												<span className="block text-[16px] leading-[21px] font-medium text-[#1F1F1F]">{row.title}</span>
												<span className="block text-[12px] leading-4 text-black/50">{row.hint}</span>
											</span>
										</label>
									</li>;
          })}
						</ul>
						<p className="mt-2 text-[12px] leading-4 text-[#1F1F1F]/64">
							Письма о заказе, оплате, отправке и возврате приходят всегда.
						</p>
					</div>}
				<PushToggle />
				{/* Частота рассылок, подборки недели, отписки от магазинов и категорий,
        Telegram — на отдельной странице (fd-notif-01/14/15, fd-platform-04). */}
				<Link to="/profile/notifications/settings" onClick={() => onOpenChange(false)} className="mt-4 flex items-center justify-between gap-2 rounded-[10px] bg-[#F8F8F8] px-3 py-2.5 text-[14px] leading-[18px] font-medium text-[#1F1F1F] outline-none hover:bg-[#989AA2]/12 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
					<span>
						Все настройки
						<span className="block text-[12px] leading-4 font-normal text-black/50">Частота рассылок, отписки, Telegram</span>
					</span>
					<ChevronRight className="size-5 shrink-0 text-[#333]" strokeWidth={1.5} aria-hidden="true" />
				</Link>
				<div className="mt-4 grid grid-cols-[1fr_auto] gap-2">
					<button type="button" disabled={saving || !draft} onClick={() => setAll(allUnchecked)} className="h-10 rounded-[10px] bg-[#989AA2]/12 px-4 text-[14px] leading-[18px] font-semibold text-[#1F1F1F] outline-none hover:bg-[#989AA2]/20 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:opacity-60">
						{allUnchecked ? "Включить все уведомления" : "Отключить все уведомления"}
					</button>
					<button type="button" disabled={saving || !draft} onClick={() => draft && void save(draft)} className="h-10 rounded-[10px] bg-[#2E2E2E] px-4 text-[14px] leading-[18px] font-semibold text-white outline-none hover:bg-[#1F1F1F] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:opacity-60">
						Сохранить
					</button>
				</div>
			</DialogContent>
		</Dialog>;
}